import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { EventDetail } from "@/components/event-detail";
import { eventQueryOptions } from "@/queries/use-events";
import { ScrollableContainer } from "@/components/ui/scrollbar";
import { EventDetailSkeleton } from "@/components/event-detail/event-detail-skeleton";
import { RouterContext } from "@/types/router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ResourceError } from "@/components/ui/resource-error";
import { useTranslation } from "react-i18next";
import { useSelectedGym } from "@/hooks/use-selected-gym";
import { EVENTABLE_TYPE } from "@/constants/events";

interface LoaderParams {
  params: { eventId: string };
  context: RouterContext;
}

async function loadEventData({ params, context }: LoaderParams) {
  const selectedGym = context.gymStore.getState().selectedGym;

  if (!selectedGym) return;

  const event = await context.queryClient.ensureQueryData(
    eventQueryOptions(selectedGym.id, Number(params.eventId)),
  );

  return {
    breadcrumb: event.title,
    breadcrumbKey:
      event.eventableType === EVENTABLE_TYPE.Vacancy
        ? "pages:calendar.eventDetail.vacancy.title"
        : undefined,
    hasActionButtons: true,
    event,
  };
}

export const Route = createFileRoute("/(protected)/scheduling/$eventId/")({
  component: RouteComponent,
  loader: loadEventData,
  pendingComponent: () => <EventDetailSkeleton />,
  errorComponent: EventError,
  validateSearch: z.object({
    staffMemberId: z.coerce.number().optional(),
  }),
});

function RouteComponent() {
  const { eventId } = Route.useParams();
  const { staffMemberId } = Route.useSearch();
  const { id: selectedGymId } = useSelectedGym();

  const { data: event } = useSuspenseQuery(
    eventQueryOptions(selectedGymId, Number(eventId)),
  );
  return (
    <ScrollableContainer className="mt-0">
      <div className="flex min-h-full flex-col lg:py-2">
        {event && <EventDetail event={event} staffMemberId={staffMemberId} />}
      </div>
    </ScrollableContainer>
  );
}

function EventError({ error }: { error: Error }) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col px-6 md:px-6 lg:my-2">
      <ResourceError
        error={error}
        notFoundMessage={t("pages:calendar.errors.notFound")}
        errorMessage={t("pages:calendar.errors.loadingFailed", {
          message: error.message,
        })}
      />
    </div>
  );
}
